/**
 * The Reown wallet registry behind the "All wallets" screen (app#315, spec §5 and §9 Р1): the
 * names, marks and links of the six hundred wallets a person may hold on a phone, read from the
 * same server Reown's own window reads (`api.web3modal.org`, its `ApiController`). Kept free of
 * React and of the network so every decision in it can be tested as a plain function: what is
 * asked, how an answer is read, how a wallet's link carries the pairing, and where a wallet without
 * a link sends the person instead.
 *
 * Read on 2026-11-02 with our project id: the registry answers only with the three `x-` headers
 * below (403 without them — the marks included, so a mark cannot be an `<img src>` straight from
 * the registry and is fetched as a blob instead, the way Reown's window does); a page holds up to
 * 100 wallets (500 is refused); `search=` is matched by the server; answers carry
 * `cache-control: public, max-age=43200`, the marks a year.
 */

/** Where the registry answers from. Named in the storefront's policy too (`csp.server.ts`). */
export const REGISTRY_ORIGIN = 'https://api.web3modal.org';

/** How many wallets a page lists — the number Reown's own window asks for a page of its grid. */
export const REGISTRY_PAGE_SIZE = 40;

/** How long an answer stands in the browser: the registry's own `max-age`, twelve hours. */
export const REGISTRY_STALE_MS = 12 * 60 * 60 * 1000;

/** How long the search field waits after the last keystroke before the registry is asked. */
export const SEARCH_DEBOUNCE_MS = 300;

/**
 * What every request to the registry carries. The project id is ours (public); the sdk headers
 * are what the registry expects of a client — Reown's window sends the same pair.
 */
export const registryHeaders = (projectId: string): Record<string, string> => ({
  'x-project-id': projectId,
  'x-sdk-type': 'w3m',
  'x-sdk-version': 'html-wagmi-5.1.11',
});

/** The first page of the registry, or of the wallets whose name matches `search`. */
export const registryUrl = (search = ''): string => {
  const url = new URL('/getWallets', REGISTRY_ORIGIN);
  const term = search.trim();

  url.searchParams.set('page', '1');
  url.searchParams.set('entries', String(REGISTRY_PAGE_SIZE));
  if (term) {
    url.searchParams.set('search', term);
  }

  return url.toString();
};

/** A wallet's mark, by the id the registry names it under (a webp). */
export const registryImageUrl = (imageId: string): string =>
  `${REGISTRY_ORIGIN}/getWalletImage/${encodeURIComponent(imageId)}`;

/** A wallet as the screen needs it, read out of the registry's entry. */
export interface RegistryWallet {
  id: string;
  name: string;
  imageId: string | null;
  /** The mark, once fetched, as a `blob:` address of this page; null until then or without one. */
  icon: string | null;
  /** The app's own link: a scheme (`trust://`) for almost all, an https address for a few. */
  mobileLink: string | null;
  /** The app's universal link, where the registry has one next to the scheme. */
  linkMode: string | null;
  appStore: string | null;
  playStore: string | null;
  homepage: string | null;
}

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

/**
 * The wallets of an answer — `{count, data: [...]}` — each read for what the screen needs, in the
 * registry's order (its own ranking). An entry without an id or a name is nobody's row and is left
 * out; a field of another shape than expected reads as absent, never as a crash.
 */
export const parseRegistry = (json: unknown): RegistryWallet[] => {
  const data = (json as { data?: unknown } | null)?.data;

  if (!Array.isArray(data)) {
    return [];
  }

  return data.flatMap((entry: Record<string, unknown> | null) => {
    const id = text(entry?.id);
    const name = text(entry?.name);

    if (!id || !name || !entry) {
      return [];
    }

    return [
      {
        id,
        name,
        imageId: text(entry.image_id),
        icon: null,
        mobileLink: text(entry.mobile_link),
        linkMode: text(entry.link_mode),
        appStore: text(entry.app_store),
        playStore: text(entry.play_store),
        homepage: text(entry.homepage),
      },
    ];
  });
};

export const isHttpLink = (link: string): boolean => /^https?:\/\//i.test(link);

/**
 * A wallet app's universal link with the pairing inside: `<link>/wc?uri=<pairing, encoded>`. The
 * shape every WalletConnect wallet documents (Trust: developer.trustwallet.com, "Deep linking")
 * and the one Reown's own window builds from the registry's `universal` field — one slash,
 * however the address ends.
 */
export const walletLink = (universalLink: string, uri: string): string =>
  `${universalLink.replace(/\/+$/, '')}/wc?uri=${encodeURIComponent(uri)}`;

/**
 * A wallet app's link with the pairing inside, the way Reown's own window builds it
 * (`CoreHelperUtil.formatNativeUrl` / `formatUniversalUrl` of `@reown/appkit-controllers` 1.8,
 * mirrored here): an https address gets `/wc?uri=` after it (one slash, however the address ends);
 * a scheme gets `://` when it was written without, a `/` when it does not end in one, then
 * `wc?uri=` — `trust://` becomes `trust://wc?uri=…`, `okex://main` becomes `okex://main/wc?uri=…`.
 * The pairing is URL-encoded once in both: its `@`, `?` and `&` would otherwise break the link.
 */
export const appLink = (appUrl: string, uri: string): string => {
  if (isHttpLink(appUrl)) {
    return walletLink(appUrl, uri);
  }

  const pairing = encodeURIComponent(uri);

  let scheme = appUrl;

  if (!scheme.includes('://')) {
    scheme = `${appUrl.replace(/\//g, '').replace(/:/g, '')}://`;
  }
  if (!scheme.endsWith('/')) {
    scheme = `${scheme}/`;
  }

  return `${scheme}wc?uri=${pairing}`;
};

/**
 * The link that opens a registry wallet's app with the pairing, or null when the registry lists no
 * link for it at all. The app's own link first — the scheme, which is what Reown's window opens
 * by default — and the universal link only when there is no scheme: the two are handed to the
 * same function, so a wallet whose universal link already carries a query (OKX's) is never asked
 * to take `/wc?uri=` on top of it.
 */
export const pairingLinkOf = (wallet: RegistryWallet, uri: string): string | null => {
  const base = wallet.mobileLink ?? wallet.linkMode;

  return base ? appLink(base, uri) : null;
};

/** Whether a registry wallet can be opened with the pairing at all. */
export const hasPairingLink = (wallet: RegistryWallet): boolean =>
  wallet.mobileLink !== null || wallet.linkMode !== null;

export type Platform = 'ios' | 'android' | 'other';

/** The phone's platform, by its `User-Agent` — which store a wallet without a link is fetched from. */
export const platformOf = (userAgent: string): Platform => {
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return 'ios';
  }

  return 'other';
};

/**
 * The stores of a wallet's app and its site: the registry's, for an entry of its, or the ones
 * `phoneWallets.ts` fixes beside a row.
 */
export type StoreLinks = Pick<RegistryWallet, 'appStore' | 'playStore' | 'homepage'>;

/**
 * Where a wallet without a pairing link sends the person instead: its store on this platform,
 * the other store failing that, its site failing both — never nowhere, so a row is never dead.
 * Null only for an entry the registry knows nothing about, which is then no row at all. The same
 * pick serves the Coinbase row after an attempt nobody answered (app#389).
 */
export const storeLinkOf = (wallet: StoreLinks, platform: Platform): string | null => {
  const stores =
    platform === 'android'
      ? [wallet.playStore, wallet.appStore]
      : [wallet.appStore, wallet.playStore];

  return [...stores, wallet.homepage].find((link): link is string => link !== null) ?? null;
};

/** The wallets whose name contains what was typed, case-insensitively; all of them for nothing typed. */
export const filterByName = <T extends { name: string }>(
  wallets: readonly T[],
  query: string
): T[] => {
  const term = query.trim().toLowerCase();

  return term ? wallets.filter((wallet) => wallet.name.toLowerCase().includes(term)) : [...wallets];
};

type Fetch = typeof globalThis.fetch;

/** The first page of the registry, or the wallets matching `search`, as the screen reads them. */
export const fetchRegistry = async (
  projectId: string,
  search = '',
  fetchImpl: Fetch = globalThis.fetch
): Promise<RegistryWallet[]> => {
  const response = await fetchImpl(registryUrl(search), { headers: registryHeaders(projectId) });

  if (!response.ok) {
    throw new Error(`The wallet registry answered ${response.status}`);
  }

  return parseRegistry(await response.json());
};

/** A wallet's mark as a `blob:` address of this page — the registry hands it over with the same headers only. */
export const fetchRegistryIcon = async (
  projectId: string,
  imageId: string,
  fetchImpl: Fetch = globalThis.fetch
): Promise<string> => {
  const response = await fetchImpl(registryImageUrl(imageId), {
    headers: registryHeaders(projectId),
  });

  if (!response.ok) {
    throw new Error(`The wallet registry answered ${response.status} for a mark`);
  }

  return URL.createObjectURL(await response.blob());
};
